import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router';
import { EditionPlayer } from '@/components/whats-new/player/edition-player';
import {
  type UseNewsEditions,
  useNewsEditions,
} from '@/hooks/use-news-editions';
import { useI18nContext } from '@/i18n/i18n-react';
import type { CatalogEdition } from '@/lib/whats-new/edition-types';
import { formatDay } from '@/lib/whats-new/formatting';
import { EDITIONS } from '@/lib/whats-new/registry';

export interface WhatsNewContextValue extends UseNewsEditions {
  /** Edition shown in the player, `null` while it's closed. */
  openEditionId: string | null;
  /** Slide the player opens on (0 = the cover). */
  slideIndex: number;
  /** Opens the player and records the view (NEWS-17). */
  open: (editionId: string, slideIndex?: number) => void;
  /** Closes it and gives the focus back (NEWS-06). */
  close: () => void;
}

interface OpenState {
  editionId: string;
  slideIndex: number;
}

const WhatsNewContext = createContext<WhatsNewContextValue | null>(null);

/**
 * Focus after the player closes: whatever opened it (a "Ver", a teaser) or,
 * when it opened on its own, the page's `main` (NEWS-06).
 */
function restoreFocus(opener: HTMLElement | null) {
  if (opener?.isConnected) {
    opener.focus({ preventScroll: true });
    return;
  }
  const main = document.querySelector('main');
  if (!main) return;
  if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
  main.focus({ preventScroll: true });
}

/**
 * The open edition's player, when the admin has its module. Name and label
 * come from the catalog row ("Estreia · 4 nov", the real publication day).
 */
function OpenEditionPlayer({
  editionId,
  slideIndex,
  catalog,
  onClose,
}: {
  editionId: string;
  slideIndex: number;
  catalog: readonly CatalogEdition[];
  onClose: () => void;
}) {
  const { locale } = useI18nContext();
  if (!Object.prototype.hasOwnProperty.call(EDITIONS, editionId)) return null;
  const row = catalog.find((edition) => edition.id === editionId);
  const name = row?.name ?? editionId;
  return (
    <EditionPlayer
      edition={EDITIONS[editionId]}
      name={name}
      label={row ? `${name} · ${formatDay(row.publishedAt, locale)}` : name}
      startIndex={slideIndex}
      onClose={onClose}
    />
  );
}

/**
 * State of the edition player for the tenant pages (`/:tenantSlug`): which
 * edition is open and on which slide, and the player itself. Switching
 * stores closes it (NEWS-13).
 */
export function WhatsNewProvider({ children }: PropsWithChildren) {
  const editions = useNewsEditions();
  const { markSeen } = editions;
  const { tenantSlug } = useParams();
  const [state, setState] = useState<OpenState | null>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  const open = useCallback(
    (editionId: string, slideIndex = 0) => {
      const active = document.activeElement;
      openerRef.current =
        active instanceof HTMLElement && active !== document.body
          ? active
          : null;
      setState({ editionId, slideIndex });
      markSeen(editionId);
    },
    [markSeen]
  );

  const close = useCallback(() => setState(null), []);

  // Runs after the player has unmounted, so the focus lands outside it.
  useEffect(() => {
    if (state) {
      wasOpenRef.current = true;
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    const opener = openerRef.current;
    openerRef.current = null;
    restoreFocus(opener);
    // Closed by a press on the scrim: Radix closes on pointerdown and the
    // same press's mousedown then moves the focus to <body>; give it back.
    const retry = window.setTimeout(() => {
      if (document.activeElement === document.body) restoreFocus(opener);
    }, 0);
    return () => window.clearTimeout(retry);
  }, [state]);

  useEffect(() => {
    setState(null);
  }, [tenantSlug]);

  const value: WhatsNewContextValue = {
    ...editions,
    openEditionId: state?.editionId ?? null,
    slideIndex: state?.slideIndex ?? 0,
    open,
    close,
  };

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
      {state && (
        <OpenEditionPlayer
          key={`${state.editionId}:${state.slideIndex}`}
          editionId={state.editionId}
          slideIndex={state.slideIndex}
          catalog={editions.catalog}
          onClose={close}
        />
      )}
    </WhatsNewContext.Provider>
  );
}

export function useWhatsNew(): WhatsNewContextValue {
  const context = useContext(WhatsNewContext);
  if (!context) {
    throw new Error('useWhatsNew must be used inside <WhatsNewProvider>');
  }
  return context;
}
